import type { IProject } from '../types/Project';

/** Project-level availability: live, coming soon (preview for invited users), or off. */
export type ProjectAvailabilityStatus = 'live' | 'coming_soon' | 'off';

/** Minimal project fields needed to resolve availability and visibility status. */
export type ProjectStatusInput = Partial<Pick<IProject, 'availabilityStatus' | 'isAvailable' | 'isPrivate'>>;

/** @deprecated Use ProjectStatusInput */
export type ProjectAvailabilityInput = ProjectStatusInput;

export const VISIBILITY_LABELS = {
  public: 'Public',
  private: 'Private',
} as const;

export const VISIBILITY_COLOURS = {
  public: '#1976d2',
  private: '#3f51b5',
} as const;

export const isProjectPrivate = (project: ProjectStatusInput): boolean => project.isPrivate === true;

export const getProjectVisibilityLabel = (project: ProjectStatusInput): string =>
  isProjectPrivate(project) ? VISIBILITY_LABELS.private : VISIBILITY_LABELS.public;

export const getProjectVisibilityColour = (project: ProjectStatusInput): string =>
  isProjectPrivate(project) ? VISIBILITY_COLOURS.private : VISIBILITY_COLOURS.public;

/** Plain-language summary of who can access the project for the current visibility + availability combination. */
export const getProjectStatusSummary = (
  isPrivate: boolean,
  status: ProjectAvailabilityStatus
): string => {
  if (isPrivate) {
    if (status === 'live') {
      return 'Only invited users can access this project.';
    }
    if (status === 'coming_soon') {
      return 'Anyone can see the project as coming soon. Only invitees can see pins.';
    }
    return 'Only invited users can access. The project is marked off for everyone else.';
  }

  if (status === 'live') {
    return 'Anyone can discover and use this project.';
  }
  if (status === 'coming_soon') {
    return 'Anyone can see the project as coming soon. Pins are not available until the project is set to Live.';
  }
  return 'Hidden and unavailable to listeners.';
};

/** Derives the canonical status from stored project fields (supports legacy `isAvailable` only). */
export const getProjectAvailabilityStatus = (
  project: ProjectStatusInput
): ProjectAvailabilityStatus => {
  if (project.availabilityStatus === 'live' || project.availabilityStatus === 'coming_soon' || project.availabilityStatus === 'off') {
    return project.availabilityStatus;
  }
  return project.isAvailable !== false ? 'live' : 'off';
};

/** Firestore fields to persist for a given status. Keeps `isAvailable` in sync for legacy clients. */
export const getAvailabilityFieldsForStatus = (
  status: ProjectAvailabilityStatus
): Pick<IProject, 'availabilityStatus' | 'isAvailable'> => ({
  availabilityStatus: status,
  isAvailable: status === 'live',
});

/** Cycles live → coming soon → off → live (project list quick-toggle). */
export const cycleAvailabilityStatus = (
  current: ProjectAvailabilityStatus
): ProjectAvailabilityStatus => {
  if (current === 'live') return 'coming_soon';
  if (current === 'coming_soon') return 'off';
  return 'live';
};

export const AVAILABILITY_STATUS_LABELS: Record<ProjectAvailabilityStatus, string> = {
  live: 'Live',
  coming_soon: 'Coming Soon',
  off: 'Off',
};

export const AVAILABILITY_STATUS_COLOURS: Record<ProjectAvailabilityStatus, string> = {
  live: '#4caf50',
  coming_soon: '#ff9800',
  off: '#9e9e9e',
};
